<script setup lang="ts">
// Overrides @basecom-gmbh/pond/components/Navigation/NavigationFlyout.vue
// IDS-124: add z-20 to first div
// IDS-188: change overall styling
import type { Schemas } from '@shopware/api-client/api-types';

const props = defineProps<{
    navigationElement: Schemas['Category'];
    parentHovered: boolean;
}>();

const flyout = ref(null);
const { isOutside: isOutsideFlyout } = useMouseInElement(flyout);
const selfNotHovered = refDebounced(isOutsideFlyout, 200);

// IDS-188: set flyout to manufacturer flyout if one category contains a supplier id
const isSupplierCategory = computed(
    () =>
        props.navigationElement.children?.filter(
            (item: Schemas['Category']) => item.translated.customFields.supplier_id !== undefined,
        ).length > 0,
);
</script>

<template>
    <Teleport to="#flyouts">
        <div
            v-show="parentHovered || !selfNotHovered"
            ref="flyout"
            class="bg-white absolute left-0 top-full z-20 w-full py-8 shadow-md"
            :data-flyout-category="navigationElement.id"
        >
            <!-- IDS-188: use 5 cols if we show the supplier flyout -->
            <div
                class="container grid gap-4"
                :class="{
                    'grid-cols-5': isSupplierCategory,
                    'grid-cols-4': !isSupplierCategory,
                }"
            >
                <div
                    v-for="child in navigationElement.children"
                    :key="`nav-item-${child.id}`"
                    class="flex flex-col"
                >
                    <!-- IDS-188: styling for a "normal" flyout" -->
                    <template v-if="!child.translated.customFields.supplier_id">
                        <!-- IDS-188: add image (if available) -->
                        <div
                            v-if="child.media"
                            class="mb-2 bg-neutral-300 p-2"
                        >
                            <NavigationLink
                                :navigation-element="child"
                                classes="flex h-[8.5rem] items-center justify-center"
                                :as-link="child.type !== 'folder'"
                                :use-slot="true"
                            >
                                <img
                                    :src="child.media.url"
                                    :alt="child.media.translated.alt"
                                    class="h-full max-w-full object-contain"
                                />
                            </NavigationLink>
                        </div>

                        <!-- IDS-188: set hover to text color -->
                        <NavigationLink
                            :navigation-element="child"
                            classes="text-lg font-bold py-2 !text-neutral-800 text-md hover:underline"
                            active-classes="text-brand-primary"
                            :as-link="child.type !== 'folder'"
                        />

                        <div
                            v-if="child.childCount > 0"
                            class="flex flex-col"
                        >
                            <!-- IDS-188: set hover to text color -->
                            <NavigationLink
                                v-for="subChild in child.children"
                                :key="`nav-item-${subChild.id}`"
                                :navigation-element="subChild"
                                classes="py-2 !text-neutral-800 hover:underline"
                                active-classes="text-brand-primary"
                                :as-link="subChild.type !== 'folder'"
                            />
                        </div>
                    </template>

                    <!-- IDS-188: styling for a manufacturer flyout -->
                    <NavigationFlyoutSupplier
                        v-else
                        :navigation-element="child"
                        :supplier-id="child.translated.customFields.supplier_id"
                    />
                </div>
            </div>
        </div>
    </Teleport>
</template>
